import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Activity, Moon, Apple, Pill, MessageSquare, Users, 
  Lightbulb, Clock, Check, Heart,
  ArrowRight, Mail, Menu, GraduationCap, FileText, Newspaper, Sparkles, Bug, Megaphone, Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import SidebarNavigation from '../components/SidebarNavigation';
import { formatLocalDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Apple IAP Product IDs
const TIER1_MONTHLY_ID = 'com.calmpathways.essential.monthly';
const TIER1_ANNUAL_ID = 'com.calmpathways.essential.yearly';
const TIER2_MONTHLY_ID = 'com.calmpathways.premium.monthly';
const TIER2_ANNUAL_ID = 'com.calmpathways.premium.yearly';

export const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [latestNews, setLatestNews] = useState([]);
  const [lunaEmail, setLunaEmail] = useState('');
  const [lunaSubmitting, setLunaSubmitting] = useState(false);
  const [lunaJoined, setLunaJoined] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/news/public?limit=3`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(d => setLatestNews(d.posts || []))
      .catch(() => {});
  }, []);

  const NEWS_CAT_META = {
    new_feature: { label: 'New Feature', icon: Sparkles, color: 'bg-purple-100 text-purple-700' },
    medical_resource: { label: 'Medical Resource', icon: Stethoscope, color: 'bg-blue-100 text-blue-700' },
    community: { label: 'Community', icon: Megaphone, color: 'bg-pink-100 text-pink-700' },
    bug_fix: { label: 'Bug Fix', icon: Bug, color: 'bg-amber-100 text-amber-700' },
    awareness: { label: 'Awareness', icon: Heart, color: 'bg-rose-100 text-rose-700' },
  };

  if (user) {
    navigate('/dashboard');
    return null;
  }

  const getUserIdForIAP = () => {
    if (user?.id) return user.id;

    try {
      const token = localStorage.getItem('token');
      if (!token) return null;

      const payload = token.split('.')[1];
      if (!payload) return null;

      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`;
      const decoded = JSON.parse(atob(paddedBase64));
      return decoded?.sub || null;
    } catch (error) {
      return null;
    }
  };

  const handleIAPPurchase = (action, productId) => {
    const iapHandler = window?.webkit?.messageHandlers?.iapHandler;
    const userId = getUserIdForIAP();

    if (!userId) {
      sessionStorage.setItem('pendingIAP', JSON.stringify({ action, productId }));
      navigate('/auth?redirect=iap');
      return;
    }

    if (iapHandler?.postMessage) {
      iapHandler.postMessage({
        action,
        productId,
        packageId: productId,
        userId
      });
      return;
    }

    // Fallback for normal website usage outside iOS webview.
    navigate('/pricing');
  };

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (res.ok) {
        toast.success('Welcome to our community!');
        setEmail('');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (err) {
      toast.error('Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const handleLunaWaitlistSignup = async (e) => {
    e.preventDefault();
    const trimmed = lunaEmail.trim();
    if (!trimmed) return;
    setLunaSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/luna-waitlist/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source: 'landing_page' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setLunaJoined(true);
        setLunaEmail('');
        toast.success(data.already_subscribed
          ? "You're already on the list — we'll be in touch!"
          : "You're on the list! 🌙");
      } else {
        const msg = Array.isArray(data?.detail)
          ? 'Please enter a valid email address'
          : (data?.detail || 'Failed to join the waitlist');
        toast.error(msg);
      }
    } catch (err) {
      toast.error('Network error — please try again');
    } finally {
      setLunaSubmitting(false);
    }
  };

  const features = [
    {
      icon: Activity,
      title: 'Symptom Tracking',
      description: 'Log and monitor symptoms with severity ratings, triggers, and patterns over time.'
    },
    {
      icon: Moon,
      title: 'Sleep Analysis',
      description: 'Track sleep quality, duration, and identify how rest impacts your child\'s condition.'
    },
    {
      icon: Apple,
      title: 'Food Journal',
      description: 'Document meals and discover food sensitivities that may trigger flares.'
    },
    {
      icon: Pill,
      title: 'Medication & Supplements',
      description: 'Manage treatment regimens with reminders and effectiveness tracking.'
    },
    {
      icon: MessageSquare,
      title: 'Voice Journaling',
      description: 'Speak your observations and AI transcribes and categorizes them automatically.'
    },
    {
      icon: Users,
      title: 'Community Support',
      description: 'Connect with other parents on similar journeys. Share experiences and find encouragement.'
    },
    {
      icon: Lightbulb,
      title: 'Smart Correlations',
      description: 'AI-powered analysis discovers hidden patterns between symptoms, triggers, and activities.'
    },
    {
      icon: Clock,
      title: 'Flare Predictions',
      description: 'Get warnings when conditions align for a potential flare based on your tracking data.'
    },
    {
      icon: GraduationCap,
      title: 'Teacher Observations',
      description: 'Invite your child\'s teacher to submit daily check-ins so school-day symptoms show up alongside home data.'
    },
    {
      icon: FileText,
      title: 'PDF Care Reports',
      description: 'Export polished medical timelines and lab summaries to share with your care team in one click.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      condition: 'PANDAS Parent',
      quote: 'Finally, an app that understands the complexity of PANDAS. The pattern detection helped me identify triggers I never knew my child had.'
    },
    {
      name: 'Michael R.',
      condition: 'PANS Parent',
      quote: 'The AI chat feature is like having a knowledgeable friend available 24/7. It helped me prepare better questions for our neurologist.'
    },
    {
      name: 'Jennifer L.',
      condition: 'Histamine Intolerance',
      quote: 'Tracking symptoms and correlating them with diet and sleep has been eye-opening. We\'ve reduced flare frequency significantly.'
    }
  ];

  const essentialFeatures = [
    'Symptom tracking & patterns',
    'Sleep & food logging',
    'Flare journal',
    'Knowledge base access',
    'Community forum',
    'Voice journaling with AI transcription',
    'Weather tracking & correlations',
    'Cycle/period tracker',
    'Parent & child regulation tools',
    'AI health chat (limited)',
    'Coping library'
  ];

  const premiumFeatures = [
    'Everything in Essential',
    'Unlimited AI health chat',
    'Smart correlations & trigger detection',
    'Symptom photo log with AI analysis',
    'Flare predictions & warnings',
    'Weekly health reports (Coming Soon)',
    'Medical timeline',
    'Lab results tracking',
    'Supplement & medication tracker',
    'Appointment management',
    'PDF export & reports'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Sidebar Navigation */}
      <SidebarNavigation isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} showOnLanding={true} />

      {/* Top Banner with Hamburger Menu */}
      <div className="bg-primary text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="landing-hamburger-menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <p className="text-sm md:text-base font-medium">
            Calm Pathways App by Me and My Pandas
          </p>
          <div className="w-10"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-primary fill-primary" />
            <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
              Support for PANDAS/PANS Families
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-nunito font-bold text-[#2D2D2D] leading-tight">
            Navigate Your Child's Health Journey with Confidence
          </h1>
          
          <p className="text-lg md:text-xl text-[#57534E] max-w-2xl mx-auto">
            Track symptoms, discover patterns, and take control of your child's health. Our platform helps you understand their condition and make informed decisions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              onClick={() => navigate('/pricing')}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-6 font-bold text-lg"
              data-testid="hero-start-journey"
            >
              Start Your Journey
            </Button>
            <Button
              onClick={() => navigate('/auth')}
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full px-8 py-6 font-bold text-lg"
              data-testid="hero-login"
            >
              Log In
            </Button>
          </div>
          
          <p className="text-sm text-[#78716C]">
            Cancel anytime · Upgrade whenever you need
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Everything You Need to Manage Your Child's Health
            </h2>
            <p className="text-[#57534E] max-w-2xl mx-auto">
              Comprehensive tools designed specifically for the PANDAS/PANS community
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="p-6 hover:shadow-md transition-shadow">
                <feature.icon className="w-10 h-10 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="font-bold text-[#2D2D2D] mb-2">{feature.title}</h3>
                <p className="text-sm text-[#57534E]">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Trusted by the PANDAS/PANS Community
            </h2>
            <p className="text-[#57534E]">
              Hear from real parents managing their children's conditions with Calm Pathways
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold text-lg">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-bold text-[#2D2D2D]">{t.name}</p>
                    <p className="text-sm text-primary">{t.condition}</p>
                  </div>
                </div>
                <p className="text-[#57534E] italic">"{t.quote}"</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-6 bg-white" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-[#57534E]">
              Start free and upgrade when you're ready for more
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Essential */}
            <Card className="p-6 border-2 border-stone-200">
              <h3 className="text-2xl font-bold text-[#2D2D2D] mb-2">Essential</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-[#2D2D2D]">$9</span>
                <span className="text-[#57534E]">/month</span>
              </div>
              <p className="text-sm text-[#57534E] mb-6">or $99/year (Save $9)</p>
              <p className="text-sm text-[#78716C] mb-6">Complete tracking for health management</p>
              <ul className="space-y-3 mb-6">
                {essentialFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-[#57534E]">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleIAPPurchase('choose_essential', TIER1_MONTHLY_ID)}
                className="w-full bg-primary/10 text-primary hover:bg-primary/20"
              >
                Choose Essential
              </Button>
            </Card>

            {/* Premium */}
            <Card className="p-6 border-2 border-primary relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-[#2D2D2D] mb-2">Premium</h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-[#2D2D2D]">$19</span>
                <span className="text-[#57534E]">/month</span>
              </div>
              <p className="text-sm text-[#57534E] mb-6">or $199/year (Save $29)</p>
              <p className="text-sm text-[#78716C] mb-6">Complete toolkit for optimal health outcomes</p>
              <ul className="space-y-3 mb-6">
                {premiumFeatures.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-[#57534E]">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleIAPPurchase('choose_premium', TIER2_MONTHLY_ID)}
                className="w-full bg-primary text-white hover:bg-primary/90"
              >
                Choose Premium
              </Button>
            </Card>
          </div>
        </div>
      </section>

      {/* What's New Section (Calm Pathways updates) */}
      {latestNews.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                <Newspaper className="w-4 h-4" /> What's New
              </div>
              <h2 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                Latest from the Calm Pathways team
              </h2>
              <p className="text-[#57534E] mt-2">
                We ship updates regularly. Here's what just landed.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              {latestNews.map((post) => {
                const meta = NEWS_CAT_META[post.category] || NEWS_CAT_META.new_feature;
                const Icon = meta.icon;
                return (
                  <Card key={post.id} className="p-5 hover:shadow-md transition-shadow">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 w-fit mb-3 ${meta.color}`}>
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                    <h3 className="font-nunito font-bold text-[#2D2D2D] text-lg mb-2 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-[#57534E] line-clamp-3">
                      {post.body.replace(/\*\*/g, '').replace(/\*/g, '')}
                    </p>
                    <p className="text-xs text-stone-400 mt-3">
                      {formatLocalDate(post.published_at || post.created_at, 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Sister App Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-sm font-medium text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
            Sister App
          </span>
          <h2 className="text-2xl md:text-3xl font-nunito font-bold text-[#2D2D2D] mt-4 mb-4">
            Discover Autoimmune Pathways
          </h2>
          <p className="text-[#57534E] mb-6 max-w-2xl mx-auto">
            Looking for the same comprehensive support for adult autoimmune conditions? 
            Autoimmune Pathways offers tracking, insights, and community support for conditions like 
            Lupus, Rheumatoid Arthritis, Hashimoto's, and more.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {['Lupus', 'Rheumatoid Arthritis', 'MS', 'Hashimoto\'s', 'Crohn\'s', 'Celiac', 'Psoriasis'].map((condition) => (
              <span key={condition} className="text-xs bg-white px-3 py-1 rounded-full text-[#57534E] border">
                {condition}
              </span>
            ))}
          </div>
          <Button
            onClick={() => window.open('https://autoimmune-pathways.com/', '_blank')}
            variant="outline"
            className="border-purple-600 text-purple-600 hover:bg-purple-50"
          >
            Learn More About Autoimmune Pathways
          </Button>
        </div>
      </section>

      {/* Luna Pathways - Coming Soon */}
      <section className="py-16 px-6 bg-gradient-to-br from-rose-50 via-pink-50 to-violet-50 relative overflow-hidden" data-testid="luna-pathways-section">
        {/* Decorative moon glow */}
        <div className="absolute -top-16 -right-16 w-72 h-72 bg-pink-200/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-16 w-80 h-80 bg-violet-200/40 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="text-sm font-medium text-rose-700 bg-white/80 backdrop-blur px-3 py-1 rounded-full border border-rose-200">
              Coming Soon
            </span>
            <span className="text-sm font-medium text-violet-700 bg-white/80 backdrop-blur px-3 py-1 rounded-full border border-violet-200 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> New App
            </span>
          </div>

          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-violet-500 flex items-center justify-center shadow-lg shadow-pink-200/60">
              <Moon className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-nunito font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-violet-600 bg-clip-text text-transparent">
              Luna Pathways
            </h2>
          </div>

          <p className="text-base md:text-lg text-[#2D2D2D] font-medium mb-4 max-w-2xl mx-auto">
            Understand your cycle. Work with your brain, not against it.
          </p>

          <p className="text-[#57534E] mb-6 max-w-2xl mx-auto leading-relaxed">
            Luna Pathways is a personalized tracking and insight app designed for women with ADHD
            to understand how their brain, mood, and energy shift across their menstrual cycle.
            By combining simple daily check-ins with powerful pattern recognition, Luna Pathways
            helps users identify how hormones influence focus, motivation, emotional regulation,
            and overall functioning.
          </p>

          <p className="text-[#57534E] mb-8 max-w-2xl mx-auto leading-relaxed">
            Over time, the app transforms this data into clear, actionable insights — helping
            women anticipate challenges, plan around their strengths, and feel more in control
            of their lives. Instead of wondering why things suddenly feel harder, Luna Pathways
            helps you understand your natural rhythms.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {['ADHD + Cycle Tracking', 'Daily Check-ins', 'Pattern Recognition', 'Focus & Mood Insights', 'Hormone Awareness'].map((feature) => (
              <span
                key={feature}
                className="text-xs bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-[#57534E] border border-rose-100 shadow-sm"
              >
                {feature}
              </span>
            ))}
          </div>

          {/* Waitlist form */}
          {lunaJoined ? (
            <div
              className="inline-flex flex-col items-center gap-2 px-6 py-4 bg-white/90 backdrop-blur rounded-2xl border border-violet-200 shadow-md max-w-md mx-auto"
              data-testid="luna-waitlist-success"
            >
              <div className="flex items-center gap-2 text-violet-700 font-semibold">
                <Sparkles className="w-4 h-4" />
                You're on the list!
              </div>
              <p className="text-sm text-[#57534E] text-center">
                We'll email you the moment Luna Pathways launches. 🌙
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleLunaWaitlistSignup}
              className="max-w-md mx-auto"
              data-testid="luna-waitlist-form"
            >
              <p className="text-sm font-semibold text-[#2D2D2D] mb-3 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-600" />
                Be the first to know when Luna Pathways launches
              </p>
              <div className="flex flex-col sm:flex-row gap-2 bg-white/90 backdrop-blur p-2 rounded-2xl shadow-md border border-violet-100">
                <Input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={lunaEmail}
                  onChange={(e) => setLunaEmail(e.target.value)}
                  disabled={lunaSubmitting}
                  className="flex-1 border-0 focus-visible:ring-0 bg-transparent text-[#2D2D2D] placeholder:text-[#A8A29E]"
                  data-testid="luna-waitlist-email-input"
                />
                <Button
                  type="submit"
                  disabled={lunaSubmitting || !lunaEmail.trim()}
                  className="bg-gradient-to-r from-rose-500 via-pink-500 to-violet-500 hover:from-rose-600 hover:via-pink-600 hover:to-violet-600 text-white font-semibold px-6 rounded-xl shadow-md disabled:opacity-60"
                  data-testid="luna-waitlist-submit-btn"
                >
                  {lunaSubmitting ? 'Joining…' : 'Notify Me'}
                </Button>
              </div>
              <p className="text-xs text-[#A8A29E] mt-3">
                No spam. One email at launch — that's it.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-nunito font-bold text-white mb-4">
            Ready to Take Control of Your Child's Health?
          </h2>
          <p className="text-white/80 mb-8">
            Join families managing PANDAS/PANS smarter.
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 font-bold text-lg"
          >
            Choose Your Plan
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#2D2D2D] text-white/60">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary fill-primary" />
            <span className="font-bold text-white">Calm Pathways</span>
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/faq" className="hover:text-white">FAQ's</Link>
            <Link to="/medical-disclosure" className="hover:text-white">Medical Disclaimer</Link>
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
          <p className="text-sm">© 2026 Calm Pathways. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
